import { useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ICategory } from "app/common/services/categoryService.types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { shouldEnableCompareButton } from "../../routes/routeUtils";
import type { ICategoriesData } from "../types";

export const useIndustryCategoryActions = (categoriesData: ICategoriesData, selectedCategory: ICategory | null) => {
    const { state } = useCurrentStateAndParams();
    const shouldEnableCompareBtn = shouldEnableCompareButton(state.name);

    const handleCategoryReplace = useCallback(
        (oldId: string, newCategory: ICategory) => {
            const currentParams = proNavigator.getParams();
            const currentCategoryArray = Array.isArray(currentParams.category)
                ? currentParams.category
                : [currentParams.category];

            const newCategoryArray = currentCategoryArray.map((catId: string) =>
                catId === oldId ? newCategory.forUrl || newCategory.categoryId || newCategory.id : catId,
            );

            const finalCategoryParam = shouldEnableCompareBtn ? newCategoryArray : newCategoryArray[0];

            void proNavigator.go(state.name, {
                ...currentParams,
                category: finalCategoryParam,
            });
        },
        [shouldEnableCompareBtn, state.name],
    );

    const handleCompareCategorySelect = useCallback(
        (category: ICategory) => {
            const currentParams = proNavigator.getParams();
            const currentCategoryId =
                selectedCategory?.forUrl || selectedCategory?.categoryId || categoriesData.selectedCategoryId;
            const newCategoryId = category.forUrl || category.categoryId || category.id;
            const comparisonCategories = [currentCategoryId, newCategoryId];

            const comparisonParams = {
                ...currentParams,
                category: comparisonCategories,
            };

            void proNavigator.go(state.name, comparisonParams);
        },
        [categoriesData.selectedCategoryId, selectedCategory?.categoryId, selectedCategory?.forUrl, state.name],
    );

    const handleCategoryRemove = useCallback(
        (categoryId: string) => {
            const currentParams = proNavigator.getParams();
            const currentCategories = Array.isArray(currentParams.category)
                ? currentParams.category
                : [currentParams.category];
            const updatedCategories = currentCategories.filter((catId: string) => catId !== categoryId);

            // Simple navigation - let the route redirects handle V2 logic automatically
            void proNavigator.go(state.name, {
                ...currentParams,
                category: (updatedCategories.length === 1 ? updatedCategories[0] : updatedCategories) as
                    | string
                    | string[],
            });
        },
        [state.name],
    );

    return {
        handleCategoryReplace,
        handleCompareCategorySelect,
        handleCategoryRemove,
    };
};
